import { useCallback, useEffect, useMemo, useState } from 'react';

import { PauseIcon, PlayIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useWaitingSounds, { WaitingSoundName } from '@/hooks/useWaitingSounds';

const NONE_VALUE = 'none';

interface WaitingSoundsSelectorProps {
  value?: WaitingSoundName | '';
  onChange: (value: WaitingSoundName | '') => void;
  disabled?: boolean;
}

/**
 * Selector component for waiting sounds used in Twilio Voice integration.
 * Allows users to select and preview different waiting sounds.
 */
const WaitingSoundsSelector = ({ value, onChange, disabled }: WaitingSoundsSelectorProps) => {
  const { t } = useTranslation();
  const { sounds, playSound, stopSound, isPlaying, isLoading } = useWaitingSounds();
  const [previewSound, setPreviewSound] = useState<WaitingSoundName | null>(null);

  // Cleanup: stop audio when component unmounts
  useEffect(() => {
    return () => {
      stopSound();
    };
  }, [stopSound]);

  const isCurrentlyPlaying = useMemo(
    () => previewSound !== null && isPlaying === previewSound,
    [isPlaying, previewSound]
  );

  const handlePlayStop = useCallback(() => {
    if (isCurrentlyPlaying) {
      stopSound();
      setPreviewSound(null);
    } else if (value) {
      setPreviewSound(value);
      playSound(value);
    }
  }, [isCurrentlyPlaying, value, playSound, stopSound]);

  const handleSoundChange = useCallback(
    (newValue: string) => {
      // Stop any playing sound when changing selection
      if (isPlaying) {
        stopSound();
        setPreviewSound(null);
      }
      // Convert 'none' to empty string for the API
      onChange(newValue === NONE_VALUE ? '' : (newValue as WaitingSoundName));
    },
    [onChange, isPlaying, stopSound]
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label>{t('integrations.voice_common.waiting_sounds.label')}</Label>
      </div>

      <div className="flex items-center gap-2">
        <Select value={value || NONE_VALUE} onValueChange={handleSoundChange} disabled={disabled}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={t('integrations.voice_common.waiting_sounds.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>
              {t('integrations.voice_common.waiting_sounds.none')}
            </SelectItem>
            {sounds.map((sound) => (
              <SelectItem key={sound.id} value={sound.id}>
                {sound.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="button"
          size="icon-lg"
          variant="primary"
          onClick={handlePlayStop}
          disabled={disabled || !value || isLoading}
          aria-label={
            isCurrentlyPlaying
              ? t('integrations.voice_common.waiting_sounds.stop')
              : t('integrations.voice_common.waiting_sounds.play')
          }
        >
          {isCurrentlyPlaying ? <PauseIcon className="size-5" /> : <PlayIcon className="size-5" />}
        </Button>
      </div>
    </div>
  );
};

export default WaitingSoundsSelector;
